import { message, Tooltip } from "antd";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  BooleanField,
  CommunityInteractionField,
  ContributorField,
  CreativeWorkField,
  DataSetField,
  DateField,
  EnergyComponentField,
  ExampleField,
  FundingField,
  InterfaceField,
  MonetaryAmountField,
  MultiNumberField,
  MultiSelectField,
  OptimizationToolField,
  OrganizationField,
  ParagraphField,
  ReviewField,
  ScholarlyArticleField,
  ScholarlyArticleSingleField,
  SingleSelectField,
  SoftwareApplicationField,
  TagInputField,
  TestCollectionField,
  TextField,
  ValidationField,
} from "../../../components/form";
import {
  CATEGORY_OPTIONS,
  COUNTRY_OF_ORIGIN_OPTIONS,
  COVERS_SECTOR_OPTIONS,
  CYCLE_OF_UPDATES_OPTIONS,
  DEVELOPER_STRUCTURE_OPTIONS,
  DEVELOPMENT_STATUS_OPTIONS,
  DIMENSIONS_OPTIONS,
  DOMAIN_OPTIONS,
  ENERGY_COMPONENT_TYPE_OPTIONS,
  IN_LANGUAGE_OPTIONS,
  OPERATING_SYSTEM_OPTIONS,
  PROGRAMMING_LANGUAGES,
  REALTIME_CAPABILITY_OPTIONS,
  ROLE_IN_RESEARCH_OPTIONS,
  SOFTWARE_TYPE_OPTIONS,
  STATIONARY_VS_TRANSIENT_OPTIONS,
  SUPPORTED_VOLTAGE_LEVEL_OPTIONS,
  TEMPORAL_COVERAGE_OPTIONS,
  TEMPORAL_RESOLUTION_OPTIONS,
  TYPICAL_HARDWARE_OPTIONS,
} from "../../../constants/formEnums";
import type {
  CommunityInteraction,
  CreativeWork,
  DataSet,
  EnergyComponent,
  Example,
  Grant,
  Interface,
  MonetaryAmount,
  OptimizationTool,
  Organization,
  Person,
  Review,
  ScholarlyArticle,
  SoftwareApplication,
  TestCollection,
  Validation,
} from "../../../types/formTypes";
import {
  buildERSmetaJsonLd,
  getMissingRequiredFields,
  parseERSmetaToFormData,
  REQUIRED_FIELDS,
} from "../../../utils/ersmetaFormUtils";

const TABS = [
  "General",
  "Development",
  "Licensing",
  "Research",
  "Technical",
  "Interfaces",
  "Energy Context",
  "Community",
  "Submit",
] as const;

const HeaderCard = styled.div`
  background: var(--color-dashboard-box);
  border: 1px solid var(--color-stroke);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`;

const PageHeader = styled.header`
  /* content only, spacing from HeaderCard */
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: -0.02em;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 0.9rem;
  color: #666;
  font-weight: 400;

  a {
    color: var(--color-primary);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ERSmetaAcknowledgment = styled.section`
  margin-top: 48px;
  padding: 24px;
  background: #f8f9fa;
  border: 1px solid var(--color-stroke);
  border-radius: 8px;
  font-size: 0.9rem;
  color: #444;
  line-height: 1.6;

  h3 {
    margin: 0 0 16px 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #1a1a1a;
  }

  p {
    margin: 0 0 12px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: var(--color-primary);
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  .ersmeta-meta {
    font-size: 0.85rem;
    color: #666;
    margin-top: 8px;
  }
`;

const SessionNotice = styled.span`
  display: inline-block;
  margin-top: 12px;
  font-size: 0.75rem;
  color: #888;
  font-weight: 400;
`;

const UploadSection = styled.div`
  margin-top: 12px;
  font-size: 0.8rem;
  color: #666;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

const UploadButton = styled.label`
  display: inline-block;
  padding: 6px 8px !important;
  font-size: 1rem;
  cursor: pointer;
  border-radius: 6px;
  border: none;
  background: var(--color-primary);
  color: #fff;
  font-weight: 500;
  box-sizing: border-box;

  &:hover {
    opacity: 0.9;
  }

  input {
    display: none;
  }
`;

const FormWrapper = styled.form`
  max-width: none !important;
  width: 100%;
`;

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 6px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--color-stroke);
  width: 100%;
`;

const FormContent = styled.div`
  max-width: 40em;
  margin-left: auto;
  margin-right: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 4px 36px !important;
  border: none !important;
  outline: none !important;
  border-radius: 6px;
  background: ${(p) => (p.$active ? "var(--color-primary)" : "#e8e8e8")} !important;
  color: ${(p) => (p.$active ? "#fff" : "#555")} !important;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  cursor: pointer;
  font-size: 0.9rem;
  flex: none !important;
  width: max-content;
  min-width: 100px;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--color-primary) !important;
    color: #fff !important;
  }
`;

const TabContent = styled.div`
  min-height: 200px;
  padding: 16px 0;
`;

const TabContentInner = styled.div`
  margin-top: 10px;
`;

const RequiredFieldsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  margin-bottom: 24px;
`;

const RequiredFieldItem = styled.li<{ $filled: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  margin-bottom: 2px;
  color: ${(p) => (p.$filled ? "#389e0d" : "#cf1322")};
  font-size: 1rem;
  font-weight: 400;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  .status-icon {
    flex-shrink: 0;
    width: 1em;
    font-family: inherit;
    font-size: 1rem;
  }

  .field-text {
    flex: 1;
  }
`;

const MissingFieldsSection = styled.div`
  margin-bottom: 24px;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--color-stroke);
`;

const TabNavFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--color-stroke);
`;

const TabNavButton = styled.button`
  padding: 8px 20px;
  font-size: 0.95rem;
  cursor: pointer;
  border-radius: 6px;
  border: 1px solid var(--color-stroke);
  background: #f5f5f5;
  color: #333;

  &:hover {
    background: #e8e8e8;
  }
`;

interface TabProps {
  formData: Record<string, unknown>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}

const GeneralTab: React.FC<TabProps> = ({ formData, setFormData }) => {
  const handleChange = (key: string, value: string | string[] | undefined) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <TextField
        label="Metadata Version"
        name="schemaVersion"
        value={(formData.schemaVersion as string) ?? "ERSmeta-V0.9"}
        onChange={(v) => handleChange("schemaVersion", v)}
        required
        readOnly
        tooltip="The type and version number of the metadata collected in this Form. This is prefilled and maintained by us."
      />
      <TextField
        label="Software Name"
        name="name"
        value={(formData.name as string) ?? ""}
        onChange={(v) => handleChange("name", v)}
        required
        tooltip="Official name of the software"
      />
      <TagInputField
        label="Software Website(s)"
        name="website"
        value={
          Array.isArray(formData.website) ? (formData.website as string[]) : []
        }
        onChange={(v) => handleChange("website", v)}
        tooltip="Homepage(s) of the tool. URL of the item."
      />
      <DateField
        label="Date Created"
        name="dateCreated"
        value={(formData.dateCreated as string) ?? ""}
        onChange={(v) => handleChange("dateCreated", v)}
        required
        tooltip="The date on which the software was initially created."
      />
      <DateField
        label="Date Modified"
        name="dateModified"
        value={(formData.dateModified as string) ?? ""}
        onChange={(v) => handleChange("dateModified", v)}
        tooltip="The date on which the software was most recently modified."
      />
      <MultiSelectField
        label="Country of Origin"
        name="countryOfOrigin"
        value={
          Array.isArray(formData.countryOfOrigin)
            ? (formData.countryOfOrigin as string[])
            : []
        }
        onChange={(v) => handleChange("countryOfOrigin", v)}
        options={[...COUNTRY_OF_ORIGIN_OPTIONS]}
        tooltip="Country where the software was developed. Country codes as defined by ISO 3166-1 alpha-2 should be used."
      />
      <TextField
        label="Acronym"
        name="acronym"
        value={(formData.acronym as string) ?? ""}
        onChange={(v) => handleChange("acronym", v)}
        tooltip="Abbreviation of the software name, e.g. CIM."
      />
      <TextField
        label="Identifier"
        name="identifier"
        value={(formData.identifier as string) ?? ""}
        onChange={(v) => handleChange("identifier", v)}
        tooltip="The identifier property represents any kind of identifier for any kind of Thing, such as DOI, ISBNs, GTIN codes, UUIDs etc."
      />
      <TextField
        label="URL to Logo"
        name="logo"
        value={(formData.logo as string) ?? ""}
        onChange={(v) => handleChange("logo", v)}
        tooltip="Link (URL) to the logo of the software."
      />
      <TextField
        label="Software Version"
        name="softwareVersion"
        value={(formData.softwareVersion as string) ?? ""}
        onChange={(v) => handleChange("softwareVersion", v)}
        required
        tooltip="Version of the software instance as used by the developers. e.g. 1.0.0"
      />
      <MultiSelectField
        label="Programming Languages"
        name="programmingLanguage"
        value={
          Array.isArray(formData.programmingLanguage)
            ? (formData.programmingLanguage as string[])
            : []
        }
        onChange={(v) => handleChange("programmingLanguage", v)}
        options={PROGRAMMING_LANGUAGES}
        required
        tooltip="The computer programming language(s) in which the software is written."
      />
      <MultiSelectField
        label="Software Language"
        name="inLanguage"
        value={
          Array.isArray(formData.inLanguage)
            ? (formData.inLanguage as string[])
            : []
        }
        onChange={(v) => handleChange("inLanguage", v)}
        options={IN_LANGUAGE_OPTIONS}
        tooltip="Language used within the software (e.g. variables, documentation). Standard: IETF (BCP47)"
      />
      <TextField
        label="Abstract"
        name="abstract"
        value={(formData.abstract as string) ?? ""}
        onChange={(v) => handleChange("abstract", v)}
        tooltip="A one-line summary of what the software does."
      />
      <ParagraphField
        label="Description"
        name="description"
        value={(formData.description as string) ?? ""}
        onChange={(v) => handleChange("description", v)}
        required
        tooltip="A description of the software. Max 1200 characters."
      />
      <TagInputField
        label="Keywords"
        name="keywords"
        value={
          Array.isArray(formData.keywords)
            ? (formData.keywords as string[])
            : []
        }
        onChange={(v) => handleChange("keywords", v)}
        required
        tooltip="Keywords or tags used to describe this software. e.g. NLP or Knowledge Graph."
      />
      <TextField
        label="URL to Graphical Abstract"
        name="graphicalAbstract"
        value={(formData.graphicalAbstract as string) ?? ""}
        onChange={(v) => handleChange("graphicalAbstract", v)}
        tooltip="Link (URL) to a graphical overview on the functionality of the software."
      />
      <MultiSelectField
        label="Software Type"
        name="softwareType"
        value={
          Array.isArray(formData.softwareType)
            ? (formData.softwareType as string[])
            : []
        }
        onChange={(v) => handleChange("softwareType", v)}
        options={SOFTWARE_TYPE_OPTIONS}
        required
        tooltip="Type of software application, e.g. 'script'."
      />
      <TagInputField
        label="Software Purpose"
        name="purpose"
        value={
          Array.isArray(formData.purpose) ? (formData.purpose as string[]) : []
        }
        onChange={(v) => handleChange("purpose", v)}
        tooltip="The general goal/purpose of the software."
      />
      <TagInputField
        label="Used Methods"
        name="method"
        value={
          Array.isArray(formData.method) ? (formData.method as string[]) : []
        }
        onChange={(v) => handleChange("method", v)}
        tooltip="A specific method used in the software."
      />
      <TagInputField
        label="Use Cases"
        name="useCase"
        value={
          Array.isArray(formData.useCase) ? (formData.useCase as string[]) : []
        }
        onChange={(v) => handleChange("useCase", v)}
        tooltip="Other supported use case, e.g., a research question which can be answered with the software besides the main goal."
      />
    </>
  );
};

const DevelopmentTab: React.FC<TabProps> = ({ formData, setFormData }) => {
  const handleChange = (
    key: string,
    value:
      | string
      | string[]
      | boolean
      | Person[]
      | Organization[]
      | Grant[]
      | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <SingleSelectField
        label="Development Status"
        name="developmentStatus"
        value={
          typeof formData.developmentStatus === "string"
            ? formData.developmentStatus
            : undefined
        }
        onChange={(v) => handleChange("developmentStatus", v)}
        options={DEVELOPMENT_STATUS_OPTIONS}
        required
        tooltip="Description of development status, e.g. Active, inactive, suspended. See repostatus.org"
      />
      <SingleSelectField
        label="Cycle of Updates"
        name="cycleOfUpdates"
        value={
          typeof formData.cycleOfUpdates === "string"
            ? formData.cycleOfUpdates
            : undefined
        }
        onChange={(v) => handleChange("cycleOfUpdates", v)}
        options={CYCLE_OF_UPDATES_OPTIONS}
        tooltip="How often is the software updated?"
      />
      <MultiSelectField
        label="Developer Structure"
        name="developerStructure"
        value={
          Array.isArray(formData.developerStructure)
            ? (formData.developerStructure as string[])
            : []
        }
        onChange={(v) => handleChange("developerStructure", v)}
        options={DEVELOPER_STRUCTURE_OPTIONS}
        tooltip="Structure of the development team."
      />
      <ContributorField
        label="Contributor"
        name="contributor"
        value={
          Array.isArray(formData.contributor)
            ? (formData.contributor as Person[])
            : []
        }
        onChange={(v) => handleChange("contributor", v)}
        required
        tooltip="A person who has contributed to the software. A contributor refers to anyone who aids in software development in any capacity, from coding to testing, highlighting the collaborative nature of software projects."
      />
      <ContributorField
        label="Author"
        name="author"
        value={
          Array.isArray(formData.author) ? (formData.author as Person[]) : []
        }
        onChange={(v) => handleChange("author", v)}
        itemLabel="Author"
        addButtonLabel="+ Add Author"
        tooltip="A person who has authored the software. An author in software authorship is someone who significantly contributes to the creation and development of software, including roles like coding, project management, and documentation."
      />
      <ContributorField
        label="Maintainer"
        name="maintainer"
        value={
          Array.isArray(formData.maintainer)
            ? (formData.maintainer as Person[])
            : []
        }
        onChange={(v) => handleChange("maintainer", v)}
        itemLabel="Maintainer"
        addButtonLabel="+ Add Maintainer"
        tooltip="Individual responsible for maintaining the software."
      />
      <OrganizationField
        label="Source Organization"
        name="sourceOrganization"
        value={
          Array.isArray(formData.sourceOrganization)
            ? (formData.sourceOrganization as Organization[])
            : []
        }
        onChange={(v) => handleChange("sourceOrganization", v)}
        itemLabel="Organization"
        addButtonLabel="+ Add Organization"
        tooltip="Institution which developed the software."
      />
      <FundingField
        label="Funding"
        name="funding"
        value={
          Array.isArray(formData.funding) ? (formData.funding as Grant[]) : []
        }
        onChange={(v) => handleChange("funding", v)}
        itemLabel="Grant"
        addButtonLabel="+ Add Funding"
        tooltip="Information on the funding which was used to develop the software."
      />
      <TextField
        label="Issue Reporting URL"
        name="issueTracker"
        value={(formData.issueTracker as string) ?? ""}
        onChange={(v) => handleChange("issueTracker", v)}
        tooltip="Link (URL) to software bug reporting or issue tracking system."
      />
      <TextField
        label="Download URL"
        name="downloadUrl"
        value={(formData.downloadUrl as string) ?? ""}
        onChange={(v) => handleChange("downloadUrl", v)}
        required
        tooltip="If the file can be downloaded, URL to download the binary."
      />
      <TextField
        label="Repository URL"
        name="codeRepository"
        value={(formData.codeRepository as string) ?? ""}
        onChange={(v) => handleChange("codeRepository", v)}
        tooltip="Link (URL) to the repository where the un-compiled, human readable code and related code is located (SVN, GitHub, CodePlex, institutional GitLab instance, etc.)."
      />
      <TagInputField
        label="Documentation URL(s)"
        name="documentation"
        value={
          Array.isArray(formData.documentation)
            ? (formData.documentation as string[])
            : []
        }
        onChange={(v) => handleChange("documentation", v)}
        tooltip="Link(s) (URL(s)) to the documentation describing the code and is useful for developers."
        placeholder="Type URL(s) and press Enter"
      />
      <TextField
        label="Readme URL"
        name="readme"
        value={(formData.readme as string) ?? ""}
        onChange={(v) => handleChange("readme", v)}
        tooltip="Link (URL) to the documentation describing the code and is useful for developers."
      />
      <TextField
        label="Build Instructions URL"
        name="buildInstruction"
        value={(formData.buildInstruction as string) ?? ""}
        onChange={(v) => handleChange("buildInstruction", v)}
        tooltip="Link (URL) to installation instructions/documentation."
      />
      <TextField
        label="Required Training Period"
        name="requiredTrainingPeriod"
        value={(formData.requiredTrainingPeriod as string) ?? ""}
        onChange={(v) => handleChange("requiredTrainingPeriod", v)}
        tooltip="How long would a researcher need to be able to use the software? e.g. 2 Months."
      />
      <BooleanField
        label="Support Availability"
        name="supportGiven"
        value={
          formData.supportGiven === true
            ? true
            : formData.supportGiven === false
              ? false
              : undefined
        }
        onChange={(v) => handleChange("supportGiven", v)}
        tooltip="Is support for the software provided?"
      />
    </>
  );
};

const LicensingTab: React.FC<TabProps> = ({ formData, setFormData }) => {
  const handleChange = (
    key: string,
    value:
      | string
      | string[]
      | boolean
      | CreativeWork[]
      | Organization[]
      | MonetaryAmount
      | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <CreativeWorkField
        label="License"
        name="license"
        value={
          Array.isArray(formData.license)
            ? (formData.license as CreativeWork[])
            : []
        }
        onChange={(v) => handleChange("license", v)}
        required
        itemLabel="License"
        addButtonLabel="+ Add License"
        tooltip="A license document that applies to this software."
      />
      <BooleanField
        label="Is the Software Accessible for Free?"
        name="isAccessibleForFree"
        value={
          formData.isAccessibleForFree === true
            ? true
            : formData.isAccessibleForFree === false
              ? false
              : undefined
        }
        onChange={(v) => handleChange("isAccessibleForFree", v)}
        tooltip="A flag to signal that the software is accessible for free."
      />
      <MonetaryAmountField
        label="Estimated Cost"
        name="estimatedCost"
        value={formData.estimatedCost as MonetaryAmount | undefined}
        onChange={(v) => handleChange("estimatedCost", v)}
        tooltip="Typical cost for licensing the software, if available."
      />
      <OrganizationField
        label="Copyright Holder"
        name="copyrightHolder"
        value={
          Array.isArray(formData.copyrightHolder)
            ? (formData.copyrightHolder as Organization[])
            : []
        }
        onChange={(v) => handleChange("copyrightHolder", v)}
        itemLabel="Organization"
        addButtonLabel="+ Add Organization"
        tooltip="The party holding the legal copyright to the CreativeWork."
      />
      <CreativeWorkField
        label="sd-License"
        name="sdLicense"
        value={
          Array.isArray(formData.sdLicense)
            ? (formData.sdLicense as CreativeWork[])
            : []
        }
        onChange={(v) => handleChange("sdLicense", v)}
        required
        itemLabel="License"
        addButtonLabel="+ Add License"
        tooltip="The license of the provided metadata."
      />
      <TextField
        label="sd-Publisher"
        name="sdPublisher"
        value={(formData.sdPublisher as string) ?? ""}
        onChange={(v) => handleChange("sdPublisher", v)}
        required
        tooltip="Indicates the party responsible for generating and publishing this metadata."
      />
    </>
  );
};

const ResearchTab: React.FC<TabProps> = ({ formData, setFormData }) => {
  const handleChange = (
    key: string,
    value:
      | string
      | string[]
      | ScholarlyArticle[]
      | ScholarlyArticle
      | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <MultiSelectField
        label="Research Domain"
        name="domain"
        value={
          Array.isArray(formData.domain) ? (formData.domain as string[]) : []
        }
        onChange={(v) => handleChange("domain", v)}
        options={DOMAIN_OPTIONS}
        tooltip="Research domain for which the software was written."
      />
      <MultiSelectField
        label="Research Category"
        name="category"
        value={
          Array.isArray(formData.category)
            ? (formData.category as string[])
            : []
        }
        onChange={(v) => handleChange("category", v)}
        options={CATEGORY_OPTIONS}
        tooltip="Research category for which the software was written."
      />
      <MultiSelectField
        label="Role in Research"
        name="roleInResearch"
        value={
          Array.isArray(formData.roleInResearch)
            ? (formData.roleInResearch as string[])
            : []
        }
        onChange={(v) => handleChange("roleInResearch", v)}
        options={ROLE_IN_RESEARCH_OPTIONS}
        tooltip="Kind of tasks that the software targets in the research."
      />
      <ScholarlyArticleField
        label="Reference Publication(s)"
        name="referencePublication"
        value={
          Array.isArray(formData.referencePublication)
            ? (formData.referencePublication as ScholarlyArticle[])
            : []
        }
        onChange={(v) => handleChange("referencePublication", v)}
        itemLabel="Publication"
        addButtonLabel="+ Add Publication"
        tooltip="Any academic publication which describes the software."
      />
      <ScholarlyArticleSingleField
        label="Preferred Citation"
        name="preferredCitation"
        value={formData.preferredCitation as ScholarlyArticle | undefined}
        onChange={(v) => handleChange("preferredCitation", v)}
        tooltip={
          'A reference to another work that should be cited instead of the software or dataset itself. Note that the principles of "software citation" and "data citation" require that software should be cited on the same basis as any other research product such as a paper or a book. Adding a different preferred citation may result in a violation of the respective primary principle, "Importance", when others cite this work.'
        }
      />
      <ScholarlyArticleField
        label="Used in Publication(s)"
        name="usedInPublication"
        value={
          Array.isArray(formData.usedInPublication)
            ? (formData.usedInPublication as ScholarlyArticle[])
            : []
        }
        onChange={(v) => handleChange("usedInPublication", v)}
        itemLabel="Publication"
        addButtonLabel="+ Add Publication"
        tooltip="Publications which use the software for their research."
      />
      <TagInputField
        label="Assumption(s)"
        name="assumption"
        value={
          Array.isArray(formData.assumption)
            ? (formData.assumption as string[])
            : []
        }
        onChange={(v) => handleChange("assumption", v)}
        tooltip="A required assumption of the software."
      />
      <TagInputField
        label="Limitation(s)"
        name="limitation"
        value={
          Array.isArray(formData.limitation)
            ? (formData.limitation as string[])
            : []
        }
        onChange={(v) => handleChange("limitation", v)}
        tooltip="A limitation of the software."
      />
    </>
  );
};

const TechnicalTab: React.FC<TabProps> = ({ formData, setFormData }) => {
  const handleChange = (
    key: string,
    value:
      | string
      | string[]
      | boolean
      | number[]
      | OptimizationTool[]
      | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <MultiSelectField
        label="Typical Hardware"
        name="typicalHardware"
        value={
          Array.isArray(formData.typicalHardware)
            ? (formData.typicalHardware as string[])
            : []
        }
        onChange={(v) => handleChange("typicalHardware", v)}
        options={TYPICAL_HARDWARE_OPTIONS}
        tooltip="Typical minimal hardware setup which is required to run the software."
      />
      <TextField
        label="Processor Requirements"
        name="processorRequirements"
        value={(formData.processorRequirements as string) ?? ""}
        onChange={(v) => handleChange("processorRequirements", v)}
        tooltip="Processor architecture required to run the application (e.g. IA64)."
      />
      <TextField
        label="Memory Requirements"
        name="memoryRequirements"
        value={(formData.memoryRequirements as string) ?? ""}
        onChange={(v) => handleChange("memoryRequirements", v)}
        tooltip="Minimum memory requirements (e.g. 8GB)"
      />
      <TextField
        label="Storage Requirements"
        name="storageRequirements"
        value={(formData.storageRequirements as string) ?? ""}
        onChange={(v) => handleChange("storageRequirements", v)}
        tooltip="Storage requirements (free space required). Example: 1GB"
      />
      <TextField
        label="Software File Size"
        name="fileSize"
        value={(formData.fileSize as string) ?? ""}
        onChange={(v) => handleChange("fileSize", v)}
        tooltip="Size of the application / package (e.g. 18MB). In the absence of a unit (MB, KB etc.), KB will be assumed."
      />
      <MultiNumberField
        label="Typical Execution Time"
        name="typicalExecutionTime"
        value={
          Array.isArray(formData.typicalExecutionTime)
            ? (formData.typicalExecutionTime as number[])
            : []
        }
        onChange={(v) => handleChange("typicalExecutionTime", v)}
        tooltip="Typical execution time of the software in seconds."
        placeholder="Type execution time in seconds and press Enter"
      />
      <BooleanField
        label="Parallelization Capability"
        name="parallelizationCapability"
        value={
          formData.parallelizationCapability === true
            ? true
            : formData.parallelizationCapability === false
              ? false
              : undefined
        }
        onChange={(v) => handleChange("parallelizationCapability", v)}
        tooltip="Does the software support parallelization?"
      />
      <MultiSelectField
        label="Realtime Capability"
        name="realtimeCapability"
        value={
          Array.isArray(formData.realtimeCapability)
            ? (formData.realtimeCapability as string[])
            : []
        }
        onChange={(v) => handleChange("realtimeCapability", v)}
        options={REALTIME_CAPABILITY_OPTIONS}
        tooltip="What kind of realtime simulations are supported by the software?"
      />
      <MultiSelectField
        label="Operating System"
        name="operatingSystem"
        value={
          Array.isArray(formData.operatingSystem)
            ? (formData.operatingSystem as string[])
            : []
        }
        onChange={(v) => handleChange("operatingSystem", v)}
        options={OPERATING_SYSTEM_OPTIONS}
        tooltip="The operating system(s) on which this software is supported."
      />
      <TagInputField
        label="Target Product"
        name="targetProduct"
        value={
          Array.isArray(formData.targetProduct)
            ? (formData.targetProduct as string[])
            : []
        }
        onChange={(v) => handleChange("targetProduct", v)}
        tooltip="Target programming environments to which the code applies. If applies to several versions, just the product name can be used. (e.g. Python 3.10)"
      />
      <TagInputField
        label="Required Software Dependencies"
        name="softwareRequirements"
        value={
          Array.isArray(formData.softwareRequirements)
            ? (formData.softwareRequirements as string[])
            : []
        }
        onChange={(v) => handleChange("softwareRequirements", v)}
        tooltip="Required software dependencies. (e.g. paho-mqtt>=2.1.0, python-dateutil>=2.9.0)"
      />
      <TagInputField
        label="Feature List"
        name="featureList"
        value={
          Array.isArray(formData.featureList)
            ? (formData.featureList as string[])
            : []
        }
        onChange={(v) => handleChange("featureList", v)}
        tooltip="Feature(s) of the software. (e.g. Uses OCR for document parsing)"
      />
      <TagInputField
        label="Software Parameter(s)"
        name="parameter"
        value={
          Array.isArray(formData.parameter)
            ? (formData.parameter as string[])
            : []
        }
        onChange={(v) => handleChange("parameter", v)}
        tooltip="Relevant internal parameter(s) of the software. (e.g. Simulation Batch Size)"
      />
      <OptimizationToolField
        label="Used Optimization"
        name="usedOptimization"
        value={
          Array.isArray(formData.usedOptimization)
            ? (formData.usedOptimization as OptimizationTool[])
            : []
        }
        onChange={(v) => handleChange("usedOptimization", v)}
        itemLabel="Optimization"
        addButtonLabel="+ Add Optimization"
        tooltip="Information on an optimization included in the software."
      />
    </>
  );
};

const InterfacesTab: React.FC<TabProps> = ({ formData, setFormData }) => {
  const handleChange = (
    key: string,
    value:
      | Interface[]
      | string[]
      | SoftwareApplication[]
      | DataSet[]
      | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <InterfaceField
        label="Input"
        name="input"
        value={
          Array.isArray(formData.input) ? (formData.input as Interface[]) : []
        }
        onChange={(v) => handleChange("input", v)}
        itemLabel="Interface"
        addButtonLabel="+ Add Input"
        tooltip="Description of the input functions of the software (e.g., APIs)."
      />
      <InterfaceField
        label="Output"
        name="output"
        value={
          Array.isArray(formData.output) ? (formData.output as Interface[]) : []
        }
        onChange={(v) => handleChange("output", v)}
        itemLabel="Interface"
        addButtonLabel="+ Add Output"
        tooltip="Description of the output functions of the software (e.g., APIs)."
      />
      <TagInputField
        label="Compatible Hardware"
        name="compatibleHardware"
        value={
          Array.isArray(formData.compatibleHardware)
            ? (formData.compatibleHardware as string[])
            : []
        }
        onChange={(v) => handleChange("compatibleHardware", v)}
        tooltip="Hardware which is compatible to this software (e.g., Devices or Machines in labs)."
      />
      <SoftwareApplicationField
        label="Compatible Software"
        name="compatibleSoftware"
        value={
          Array.isArray(formData.compatibleSoftware)
            ? (formData.compatibleSoftware as SoftwareApplication[])
            : []
        }
        onChange={(v) => handleChange("compatibleSoftware", v)}
        itemLabel="Software"
        addButtonLabel="+ Add Software"
        tooltip="Software tool(s) which can directly be used with the software without further changes."
      />
      <DataSetField
        label="Compatible Data"
        name="compatibleData"
        value={
          Array.isArray(formData.compatibleData)
            ? (formData.compatibleData as DataSet[])
            : []
        }
        onChange={(v) => handleChange("compatibleData", v)}
        itemLabel="Dataset"
        addButtonLabel="+ Add Dataset"
        tooltip="Data which is compatible to this software."
      />
      <DataSetField
        label="Used Data"
        name="usedData"
        value={
          Array.isArray(formData.usedData)
            ? (formData.usedData as DataSet[])
            : []
        }
        onChange={(v) => handleChange("usedData", v)}
        itemLabel="Dataset"
        addButtonLabel="+ Add Dataset"
        tooltip="Data that is directly used by the provided software."
      />
    </>
  );
};

const EnergyContextTab: React.FC<TabProps> = ({ formData, setFormData }) => {
  const handleChange = (
    key: string,
    value: EnergyComponent[] | string[] | string | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <EnergyComponentField
        label="Energy Component(s)"
        name="energyComponent"
        value={
          Array.isArray(formData.energyComponent)
            ? (formData.energyComponent as EnergyComponent[])
            : []
        }
        onChange={(v) => handleChange("energyComponent", v)}
        itemLabel="Component"
        addButtonLabel="+ Add Component"
        tooltip="Component(s) of the energy system included in the software."
      />
      <MultiSelectField
        label="Covered Sector(s)"
        name="coversSector"
        value={
          Array.isArray(formData.coversSector)
            ? (formData.coversSector as string[])
            : []
        }
        onChange={(v) => handleChange("coversSector", v)}
        options={[...COVERS_SECTOR_OPTIONS]}
        tooltip="Supported sector (s) of the software. A sector is generically dependent continuant that is a subdivision of a system."
      />
      <MultiSelectField
        label="Technology"
        name="technology"
        value={
          Array.isArray(formData.technology)
            ? (formData.technology as string[])
            : []
        }
        onChange={(v) => handleChange("technology", v)}
        options={[...ENERGY_COMPONENT_TYPE_OPTIONS]}
        tooltip="Supported energy technology of the software."
      />
      <SingleSelectField
        label="Temporal Coverage"
        name="temporalCoverage"
        value={
          typeof formData.temporalCoverage === "string"
            ? formData.temporalCoverage
            : undefined
        }
        onChange={(v) => handleChange("temporalCoverage", v)}
        options={[...TEMPORAL_COVERAGE_OPTIONS]}
        tooltip="Time scope the software was developed for."
      />
      <SingleSelectField
        label="Stationary vs Transient"
        name="stationaryVsTransient"
        value={
          typeof formData.stationaryVsTransient === "string"
            ? formData.stationaryVsTransient
            : undefined
        }
        onChange={(v) => handleChange("stationaryVsTransient", v)}
        options={[...STATIONARY_VS_TRANSIENT_OPTIONS]}
        tooltip="Does the software support stationary or transient effects?"
      />
      <MultiSelectField
        label="Temporal Resolution"
        name="hasTemporalResolution"
        value={
          Array.isArray(formData.hasTemporalResolution)
            ? (formData.hasTemporalResolution as string[])
            : []
        }
        onChange={(v) => handleChange("hasTemporalResolution", v)}
        options={[...TEMPORAL_RESOLUTION_OPTIONS]}
        tooltip="Supported temporal resolution of the software. Temporal resolution is the resolution that indicates time-dependent the level of detail."
      />
      <TagInputField
        label="Spatial Resolution"
        name="hasSpatialResolution"
        value={
          Array.isArray(formData.hasSpatialResolution)
            ? (formData.hasSpatialResolution as string[])
            : []
        }
        onChange={(v) => handleChange("hasSpatialResolution", v)}
        tooltip="Supported spatial resolution of the software. Spatial resolution is the resolution that indicates the geographical level of detail."
      />
      <MultiSelectField
        label="Dimensions"
        name="dimensions"
        value={
          Array.isArray(formData.dimensions)
            ? (formData.dimensions as string[])
            : []
        }
        onChange={(v) => handleChange("dimensions", v)}
        options={[...DIMENSIONS_OPTIONS]}
        tooltip="Supported dimensions of the software."
      />
      <TextField
        label="Spatial Scope"
        name="spatialScope"
        value={(formData.spatialScope as string) ?? ""}
        onChange={(v) => handleChange("spatialScope", v)}
        tooltip="Geo scope the software was developed for (e.g., microgrid, Germany, Europe)."
      />
      <TagInputField
        label="Spatial Coverage"
        name="spatialCoverage"
        value={
          Array.isArray(formData.spatialCoverage)
            ? (formData.spatialCoverage as string[])
            : []
        }
        onChange={(v) => handleChange("spatialCoverage", v)}
        tooltip="Regions covered by the model. Leave this blank if the model and data are not limited to a specific region. Examples include: USA, Canada, Germany, western Europe."
      />
      <MultiSelectField
        label="Supported Voltage Level(s)"
        name="supportedVoltageLevel"
        value={
          Array.isArray(formData.supportedVoltageLevel)
            ? (formData.supportedVoltageLevel as string[])
            : []
        }
        onChange={(v) => handleChange("supportedVoltageLevel", v)}
        options={[...SUPPORTED_VOLTAGE_LEVEL_OPTIONS]}
        tooltip="Supported voltage level of the software."
      />
    </>
  );
};

const CommunityTab: React.FC<TabProps> = ({ formData, setFormData }) => {
  const handleChange = (
    key: string,
    value:
      | CommunityInteraction[]
      | Review[]
      | TestCollection[]
      | Validation[]
      | Example[]
      | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <CommunityInteractionField
        label="Community Interaction(s)"
        name="communityInteractions"
        value={
          Array.isArray(formData.communityInteractions)
            ? (formData.communityInteractions as CommunityInteraction[])
            : []
        }
        onChange={(v) => handleChange("communityInteractions", v)}
        itemLabel="Interaction"
        addButtonLabel="+ Add Interaction"
        tooltip="Ways how the software developers interact with the software users (community)."
      />
      <ReviewField
        label="Review(s)"
        name="review"
        value={
          Array.isArray(formData.review) ? (formData.review as Review[]) : []
        }
        onChange={(v) => handleChange("review", v)}
        itemLabel="Review"
        addButtonLabel="+ Add Review"
        tooltip="Review(s) of the software."
      />
      <TestCollectionField
        label="Test(s)"
        name="tests"
        value={
          Array.isArray(formData.tests)
            ? (formData.tests as TestCollection[])
            : []
        }
        onChange={(v) => handleChange("tests", v)}
        itemLabel="Test"
        addButtonLabel="+ Add Test"
        tooltip="Information on the tests of the software."
      />
      <ValidationField
        label="Validation"
        name="validation"
        value={
          Array.isArray(formData.validation)
            ? (formData.validation as Validation[])
            : []
        }
        onChange={(v) => handleChange("validation", v)}
        itemLabel="Validation"
        addButtonLabel="+ Add Validation"
        tooltip="Information on validations done for the software. Validation refers to whether the software produces results that correspond to real-world properties."
      />
      <ExampleField
        label="Example Code"
        name="example"
        value={
          Array.isArray(formData.example) ? (formData.example as Example[]) : []
        }
        onChange={(v) => handleChange("example", v)}
        itemLabel="Example"
        addButtonLabel="+ Add Example"
        tooltip="Code examples for using the software."
      />
    </>
  );
};

const TAB_NAME_TO_INDEX: Record<string, number> = TABS.reduce(
  (acc, name, i) => ({ ...acc, [name]: i }),
  {},
);

interface SubmitTabProps extends TabProps {
  onNavigateToTab: (index: number) => void;
}

const SubmitTab: React.FC<SubmitTabProps> = ({ formData, onNavigateToTab }) => {
  const missingFields = useMemo(
    () => getMissingRequiredFields(formData),
    [formData],
  );
  const missingKeys = useMemo(
    () => new Set(missingFields.map((f) => f.key)),
    [missingFields],
  );
  const canSubmit = missingFields.length === 0;

  const handleGenerate = () => {
    const missing = getMissingRequiredFields(formData);
    if (missing.length > 0) {
      message.warning("Please fill the following required fields shown above.");
      return;
    }
    const jsonLd = buildERSmetaJsonLd(formData);
    const blob = new Blob([JSON.stringify(jsonLd, null, 2)], {
      type: "application/ld+json",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "metadata.jsonld";
    link.click();
    URL.revokeObjectURL(link.href);
    message.success("Metadata JSON-LD generated successfully.");
  };

  const handleFieldClick = (tab: string) => {
    const index = TAB_NAME_TO_INDEX[tab];
    if (index !== undefined && index !== 8) onNavigateToTab(index);
  };

  return (
    <MissingFieldsSection>
      <SubmitButtonWrapper
        style={{
          marginTop: 0,
          paddingTop: 0,
          borderTop: "none",
          marginBottom: 24,
        }}
      >
        <Tooltip title={!canSubmit ? "Required fields are missing." : ""}>
          <span style={{ display: "inline-block" }}>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canSubmit}
              style={{
                padding: "10px 24px",
                fontSize: "1rem",
                cursor: canSubmit ? "pointer" : "not-allowed",
              }}
            >
              Generate JSON-LD
            </button>
          </span>
        </Tooltip>
      </SubmitButtonWrapper>
      <h3 style={{ marginBottom: 12 }}>Required fields status</h3>
      <RequiredFieldsList>
        {REQUIRED_FIELDS.map((f) => {
          const filled = !missingKeys.has(f.key);
          return (
            <RequiredFieldItem
              key={f.key}
              $filled={filled}
              onClick={() => handleFieldClick(f.tab)}
              title={`Click to go to ${f.tab} tab`}
            >
              <span className="status-icon">{filled ? "•" : "x"}</span>
              <span className="field-text">
                {f.tab} → {f.label}
              </span>
            </RequiredFieldItem>
          );
        })}
      </RequiredFieldsList>
    </MissingFieldsSection>
  );
};

const GenerateGeneral: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>({
    schemaVersion: "ERSmeta-V0.9",
  });
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result as string) as Record<
          string,
          unknown
        >;
        const parsed = parseERSmetaToFormData(json);
        setFormData(parsed);
        message.success("ERSmeta JSON loaded. Form has been filled.");
      } catch (_err) {
        message.error(
          "Invalid file. Please upload a valid ERSmeta JSON or JSON-LD.",
        );
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <HeaderCard>
        <PageHeader>
          <PageTitle>Generate General Software Metadata</PageTitle>
          <PageSubtitle>
            <a
              href="https://github.com/NFDI4Energy/ERSmeta"
              target="_blank"
              rel="noopener noreferrer"
            >
              ERSmeta
            </a>{" "}
            compliant description of your software
          </PageSubtitle>
          <SessionNotice>
            Form data is not saved between browser sessions. Avoid refreshing
            before generating your file.
          </SessionNotice>
          <UploadSection>
            <div>Want to upload and edit existing data?</div>
            <UploadRow>
              <UploadButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,.jsonld,application/json,application/ld+json"
                  onChange={handleUpload}
                />
                Upload
              </UploadButton>
              <span style={{ fontSize: "0.75rem", color: "#999" }}>
                (JSON or JSON-LD. Current form will be overwritten)
              </span>
            </UploadRow>
          </UploadSection>
        </PageHeader>
      </HeaderCard>

      <FormWrapper onSubmit={(e) => e.preventDefault()}>
        <TabBar role="tablist">
          {TABS.map((label, index) => (
            <Tab
              key={label}
              type="button"
              role="tab"
              aria-selected={activeTab === index}
              aria-controls={`tabpanel-${index}`}
              id={`tab-${index}`}
              $active={activeTab === index}
              onClick={() => setActiveTab(index)}
            >
              {label === "Submit" ? "SUBMIT" : label}
            </Tab>
          ))}
        </TabBar>

        <FormContent>
          <TabContent
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            <fieldset>
              {TABS[activeTab] !== "Submit" && (
                <legend>{TABS[activeTab]}</legend>
              )}
              <TabContentInner>
                {activeTab === 0 && (
                  <GeneralTab formData={formData} setFormData={setFormData} />
                )}
                {activeTab === 1 && (
                  <DevelopmentTab
                    formData={formData}
                    setFormData={setFormData}
                  />
                )}
                {activeTab === 2 && (
                  <LicensingTab formData={formData} setFormData={setFormData} />
                )}
                {activeTab === 3 && (
                  <ResearchTab formData={formData} setFormData={setFormData} />
                )}
                {activeTab === 4 && (
                  <TechnicalTab formData={formData} setFormData={setFormData} />
                )}
                {activeTab === 5 && (
                  <InterfacesTab
                    formData={formData}
                    setFormData={setFormData}
                  />
                )}
                {activeTab === 6 && (
                  <EnergyContextTab
                    formData={formData}
                    setFormData={setFormData}
                  />
                )}
                {activeTab === 7 && (
                  <CommunityTab formData={formData} setFormData={setFormData} />
                )}
                {activeTab === 8 && (
                  <SubmitTab
                    formData={formData}
                    setFormData={setFormData}
                    onNavigateToTab={setActiveTab}
                  />
                )}
              </TabContentInner>
              <TabNavFooter>
                {activeTab > 0 ? (
                  <TabNavButton
                    type="button"
                    onClick={() => {
                      setActiveTab(activeTab - 1);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    ← Previous Section
                  </TabNavButton>
                ) : (
                  <span />
                )}
                {activeTab < TABS.length - 1 ? (
                  <TabNavButton
                    type="button"
                    onClick={() => {
                      setActiveTab(activeTab + 1);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Next Section →
                  </TabNavButton>
                ) : (
                  <span />
                )}
              </TabNavFooter>
            </fieldset>
          </TabContent>
        </FormContent>
      </FormWrapper>

      <ERSmetaAcknowledgment>
        <h3>About ERSmeta</h3>
        <p>
          <strong>ERSmeta</strong> is a metadata schema designed to facilitate
          the discovery, sharing, and reuse of energy research software. The
          schema provides a standardized way to describe the metadata associated
          with energy research software, including its purpose, functionality,
          and usage.
        </p>
        <p>
          <a
            href="https://github.com/NFDI4Energy/ERSmeta"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub
          </a>
          {" · "}
          <a
            href="https://zenodo.org/records/17465772"
            target="_blank"
            rel="noopener noreferrer"
          >
            Zenodo
          </a>
        </p>
        <p>
          <strong>License:</strong> CC BY-SA 4.0 (Creative Commons
          Attribution-ShareAlike). This license allows reusers to distribute,
          remix, adapt, and build upon the material in any medium or format, so
          long as attribution is given to the creator. The license allows for
          commercial use. If you remix, adapt, or build upon the material, you
          must license the modified material under identical terms.
        </p>
        <p>
          <strong>Reference:</strong>{" "}
          <a
            href="https://arxiv.org/pdf/2601.09456"
            target="_blank"
            rel="noopener noreferrer"
          >
            Towards a Metadata Schema for Energy Research Software
          </a>{" "}
          — S. Ferenz, O. Werth, and A. Nieße, arXiv preprint arXiv:2601.09456,
          Jan. 2026.
        </p>
        <p className="ersmeta-meta">
          S. Ferenz, O. Werth, and A. Nieße, &quot;Towards a metadata schema for
          energy research software,&quot; arXiv preprint arXiv:2601.09456, Jan.
          2026. [Online]. Available: https://arxiv.org/abs/2601.09456
        </p>
      </ERSmetaAcknowledgment>
    </div>
  );
};

export default GenerateGeneral;
